import Phaser from 'phaser';
import { GameSettings as GameSettingsManager, GameSettingsData } from '../utils/GameSettings';

/**
 * Game settings stored in local storage
 * @deprecated This interface is deprecated, use GameSettingsData instead
 */
interface LocalGameSettings {
  fps: number;
  soundVolume: number;
  musicVolume: number;
  showFps: boolean;
  playerName: string;
}

/**
 * Default settings when none are saved
 */
const DEFAULT_SETTINGS: LocalGameSettings = {
  fps: 60,
  soundVolume: 0.7,
  musicVolume: 0.5,
  showFps: false,
  playerName: 'Player'
};

export class SettingsScene extends Phaser.Scene {
  // UI elements
  private panel!: Phaser.GameObjects.Rectangle;
  private title!: Phaser.GameObjects.Text;
  private closeButton!: Phaser.GameObjects.Text;
  
  // Settings elements
  private fpsText!: Phaser.GameObjects.Text;
  private fpsOptions!: Phaser.GameObjects.Text[];
  private fpsSlider!: Phaser.GameObjects.Graphics;
  private fpsSliderHandle!: Phaser.GameObjects.Rectangle;
  
  // Player name input
  private playerNameInput!: Phaser.GameObjects.DOMElement;
  private playerNameLabel!: Phaser.GameObjects.Text;
  
  // Settings manager
  private settingsManager!: GameSettingsManager;
  private settings!: GameSettingsData;
  
  // Key-value map for FPS options
  private fpsValues: number[] = [30, 45, 60, 75, 90, 120];
  
  constructor() {
    super({ key: 'SettingsScene' });
  }

  init(): void {
    // Initialize settings
    this.settingsManager = GameSettingsManager.getInstance();
    this.settings = this.settingsManager.getSettings();
  }
  
  create(): void {
    console.log('SettingsScene create method called');
    
    // Create semi-transparent background overlay
    this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive()  // Capture clicks to prevent clicking through to main menu
      .on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        // Only close if clicking outside the panel
        const bounds = this.panel.getBounds();
        if (!Phaser.Geom.Rectangle.Contains(bounds, pointer.x, pointer.y)) {
          this.closeSettings();
        }
      });
    
    // Create settings panel
    this.createPanel();
    
    // Add FPS settings
    this.createFpsSettings();
    
    // Add player name input
    this.createPlayerNameInput();
    
    // Add close button
    this.createCloseButton();
    
    // Listen for window resize events
    this.events.on('windowResized', this.handleWindowResize, this);
  }
  
  /**
   * Create the main settings panel
   */
  private createPanel(): void {
    const width = 500;
    const height = 400;
    const x = this.scale.width / 2;
    const y = this.scale.height / 2;
    
    // Main panel background
    this.panel = this.add.rectangle(x, y, width, height, 0x333366, 0.95)
      .setStrokeStyle(2, 0x6666cc);
    
    // Title
    this.title = this.add.text(x, y - height / 2 + 30, 'Settings', {
      fontSize: '32px',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
  }
  
  /**
   * Create FPS settings controls
   */
  private createFpsSettings(): void {
    const panelX = this.panel.x;
    // Start settings options 10-20px lower from the "Settings" title
    const startY = this.panel.y - 60; // Adjusted from -80 to -60 (20px lower)
    
    // Create FPS slider
    this.createFpsSlider(panelX, startY);
    
    // Create FPS display toggle - on the same line, not stacked
    this.createFpsDisplayToggle(panelX, startY + 90);
  }
  
  /**
   * Create FPS slider control
   */
  private createFpsSlider(x: number, y: number): void {
    const sliderWidth = 300;
    const sliderHeight = 8;
    
    // FPS Label - aligned to the left at the start of the slider
    this.fpsText = this.add.text(x - sliderWidth/2, y - 40, 'Frame Rate', {
      fontSize: '24px',
      color: '#ffffff'
    }).setOrigin(0, 0.5); // Left-aligned with 0 x-origin
    
    // Current FPS value display - increased distance from slider label (from -10 to -15)
    const currentFps = this.add.text(x, y - 15, `${this.settings.fps} FPS`, {
      fontSize: '20px',
      color: '#aaaaff'
    }).setOrigin(0.5);
    
    // Draw slider background
    this.fpsSlider = this.add.graphics();
    this.fpsSlider.fillStyle(0x666666, 1);
    this.fpsSlider.fillRect(x - sliderWidth/2, y - sliderHeight/2, sliderWidth, sliderHeight);
    
    // Calculate initial handle position
    const initialPos = this.getFpsSliderPosition(this.settings.fps);
    
    // Create draggable handle
    this.fpsSliderHandle = this.add.rectangle(
      initialPos, 
      y, 
      20, 
      20, 
      0x3399ff
    ).setInteractive({ draggable: true });
    
    // Handle dragging
    this.fpsSliderHandle.on('drag', (pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      // Constrain horizontal movement to slider bounds
      const minX = x - sliderWidth/2;
      const maxX = x + sliderWidth/2;
      
      const newX = Phaser.Math.Clamp(dragX, minX, maxX);
      this.fpsSliderHandle.x = newX;
      
      // Calculate FPS value based on position
      const percentage = (newX - minX) / sliderWidth;
      const fps = this.getFpsFromPercentage(percentage);
      
      // Update settings and display
      this.settings.fps = fps;
      this.settingsManager.updateSetting('fps', fps);
      currentFps.setText(`${fps} FPS`);
      
      // Apply FPS setting immediately
      this.applyFpsSetting(fps);
    });
    
    // Add labels for min and max
    this.add.text(x - sliderWidth/2, y + 20, '30', {
      fontSize: '16px',
      color: '#aaaaaa'
    }).setOrigin(0.5);
    
    this.add.text(x + sliderWidth/2, y + 20, '120', {
      fontSize: '16px',
      color: '#aaaaaa'
    }).setOrigin(0.5);
  }
  
  /**
   * Create a toggle for FPS display
   */
  private createFpsDisplayToggle(x: number, y: number): void {
    const sliderWidth = 300;
    
    // FPS display label - on the same line as the toggle
    this.add.text(x - sliderWidth/2, y, 'Show FPS Display', {
      fontSize: '24px',
      color: '#ffffff'
    }).setOrigin(0, 0.5); // Left-aligned with 0 x-origin
    
    // Toggle background (checkbox style) - increased right margin from +100 to +150
    const toggleBg = this.add.rectangle(x + 150, y, 30, 30, 0x444444)
      .setStrokeStyle(2, 0x666666);
    
    // Toggle indicator - positioned at the same x as toggleBg
    const toggleIndicator = this.add.rectangle(x + 150, y, 20, 20, 0x3399ff)
      .setVisible(this.settings.showFps);
    
    // Make toggle interactive
    toggleBg.setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        // Toggle setting
        this.settings.showFps = !this.settings.showFps;
        
        // Update visual
        toggleIndicator.setVisible(this.settings.showFps);
        
        // Update setting
        this.settingsManager.updateSetting('showFps', this.settings.showFps);
        
        // Emit event to all active scenes to toggle FPS display
        this.notifyFpsDisplayChange(this.settings.showFps);
      });
  }
  
  /**
   * Notify all active scenes about FPS display change
   */
  private notifyFpsDisplayChange(showFps: boolean): void {
    // Get all active scenes
    const activeScenes = this.scene.manager.getScenes(true);
    const sceneManager = this.scene.manager;
    
    // Get all scene keys, including inactive ones
    const allSceneKeys = Object.keys(sceneManager.keys);
    
    console.log(`Notifying about FPS display change (${showFps}). Active scenes: ${activeScenes.length}, All scenes: ${allSceneKeys.length}`);
    
    // First notify all active scenes
    for (const scene of activeScenes) {
      if (scene !== this) {
        console.log(`Emitting toggleFpsDisplay to active scene: ${(scene as Phaser.Scene).scene.key}`);
        scene.events.emit('toggleFpsDisplay', showFps);
      }
    }
    
    // Then make sure we notify all scenes that are in the manager but might be inactive
    for (const key of allSceneKeys) {
      // Skip the settings scene itself
      if (key === 'SettingsScene') continue;
      
      const scene = sceneManager.getScene(key);
      if (scene && !activeScenes.includes(scene)) {
        console.log(`Emitting toggleFpsDisplay to inactive scene: ${key}`);
        scene.events.emit('toggleFpsDisplay', showFps);
      }
    }
    
    // For extra safety, also store this setting globally for when new scenes are created
    this.settingsManager.updateSetting('showFps', showFps);
    this.settingsManager.saveSettings();
    
    console.log(`FPS display change notification complete. Setting is now: ${showFps ? 'enabled' : 'disabled'}`);
  }
  
  /**
   * Create close button
   */
  private createCloseButton(): void {
    // Add save & close button at the bottom
    this.closeButton = this.add.text(
      this.panel.x, 
      this.panel.y + this.panel.height / 2 - 40, 
      'Save & Close', 
      {
        fontSize: '24px',
        color: '#ffffff',
        backgroundColor: '#226622',
        padding: { x: 20, y: 10 }
      }
    ).setOrigin(0.5)
     .setInteractive({ useHandCursor: true })
     .on('pointerover', () => this.closeButton.setStyle({ color: '#aaffaa' }))
     .on('pointerout', () => this.closeButton.setStyle({ color: '#ffffff' }))
     .on('pointerdown', () => {
       // Close the settings menu
       this.closeSettings();
     });
  }
  
  /**
   * Convert FPS value to slider position
   */
  private getFpsSliderPosition(fps: number): number {
    const sliderWidth = 300;
    const minX = this.panel.x - sliderWidth/2;
    
    // Find the percentage in the FPS range (30-120)
    const percentage = this.getPercentageFromFps(fps);
    
    // Convert to position
    return minX + (sliderWidth * percentage);
  }
  
  /**
   * Calculate percentage based on FPS value
   */
  private getPercentageFromFps(fps: number): number {
    const min = 30;
    const max = 120;
    return (fps - min) / (max - min);
  }
  
  /**
   * Calculate FPS based on slider percentage
   */
  private getFpsFromPercentage(percentage: number): number {
    const min = 30;
    const max = 120;
    
    // Calculate raw FPS
    const rawFps = min + (percentage * (max - min));
    
    // Find the closest valid FPS value
    let closestFps = this.fpsValues[0];
    let closestDiff = Math.abs(rawFps - closestFps);
    
    for (const fps of this.fpsValues) {
      const diff = Math.abs(rawFps - fps);
      if (diff < closestDiff) {
        closestFps = fps;
        closestDiff = diff;
      }
    }
    
    return closestFps;
  }
  
  /**
   * Apply FPS setting to the game
   */
  private applyFpsSetting(fps: number): void {
    // Update settings
    this.settingsManager.updateSetting('fps', fps);
    this.settingsManager.saveSettings();
    
    // Apply FPS to game loop
    if (this.game && this.game.loop) {
      this.game.loop.targetFps = fps;
      console.log(`Applied FPS setting to game loop: ${fps}`);
    }
    
    // Notify other scenes about the FPS change, focusing on Boot scene
    // which will handle the global FPS limiting
    this.notifyFpsRateChange(fps);
    
    console.log(`FPS set to ${fps}. Changes will apply to all scenes.`);
  }
  
  /**
   * Notify all scenes about FPS rate change
   */
  private notifyFpsRateChange(fps: number): void {
    // Ensure the Boot scene receives the message (it has our requestAnimationFrame override)
    const bootScene = this.scene.manager.getScene('BootScene');
    if (bootScene) {
      console.log('Sending fpsRateChanged event to BootScene');
      bootScene.events.emit('fpsRateChanged', fps);
    } else {
      console.warn('BootScene not found for FPS rate change notification');
    }
    
    // Get all active scenes
    const activeScenes = this.scene.manager.getScenes(true);
    
    // Emit event to each scene
    for (const scene of activeScenes) {
      if (scene !== this && scene !== bootScene) {
        console.log(`Notifying scene ${(scene as Phaser.Scene).scene.key} about FPS rate change to ${fps}`);
        scene.events.emit('fpsRateChanged', fps);
      }
    }
  }
  
  /**
   * Close the settings menu
   */
  private closeSettings(): void {
    // Save settings
    this.settingsManager.saveSettings();
    
    // Remove event listeners
    this.events.off('windowResized', this.handleWindowResize);
    
    // Resume main menu and stop settings scene
    this.scene.resume('MainMenuScene');
    this.scene.stop('SettingsScene');
  }
  
  /**
   * Load settings from localStorage
   * @deprecated Use GameSettingsManager.loadSettings instead
   */
  private loadSettings(): LocalGameSettings {
    try {
      const savedSettings = localStorage.getItem('cytopodSettings');
      if (savedSettings) {
        return JSON.parse(savedSettings);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    
    return { ...DEFAULT_SETTINGS };
  }
  
  /**
   * Save settings to localStorage
   * @deprecated Use GameSettingsManager.saveSettings instead
   */
  private saveSettings(): void {
    try {
      localStorage.setItem('cytopodSettings', JSON.stringify(this.settings));
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }
  
  /**
   * Handle window resize event
   */
  handleWindowResize(size: { width: number, height: number }): void {
    console.log(`SettingsScene handling window resize: ${size.width}x${size.height}`);
    
    // Recalculate panel position
    const x = size.width / 2;
    const y = size.height / 2;
    
    // Update panel position
    if (this.panel) {
      this.panel.setPosition(x, y);
    }
    
    // Update title position
    if (this.title) {
      this.title.setPosition(x, y - this.panel.height / 2 + 30);
    }
    
    // Update close button position
    if (this.closeButton) {
      this.closeButton.setPosition(x, y + this.panel.height / 2 - 40);
    }
    
    // Update player name input position if it exists
    if (this.playerNameLabel && this.playerNameInput) {
      const startY = y - 60;
      const inputY = startY + 180;
      
      this.playerNameLabel.setPosition(x - 150, inputY);
      this.playerNameInput.setPosition(x + 100, inputY);
    }
    
    // Recreate the settings UI with new positions
    if (this.panel && this.panel.width) {
      // Only recreate if the panel exists and has been properly initialized
      this.createFpsSettings();
    }
  }

  /**
   * Create player name input field
   */
  private createPlayerNameInput(): void {
    const panelX = this.panel.x;
    // Position below FPS toggle (which is at startY + 90)
    const startY = this.panel.y - 60;
    const inputY = startY + 180; // 90px below FPS toggle
    
    // Player name label
    this.playerNameLabel = this.add.text(panelX - 150, inputY, 'Player Name', {
      fontSize: '24px',
      color: '#ffffff'
    }).setOrigin(0, 0.5);
    
    // Create HTML input element
    const inputElement = document.createElement('input');
    inputElement.type = 'text';
    inputElement.id = 'settings-player-name-input';
    inputElement.name = 'input';
    inputElement.placeholder = 'Enter your name';
    inputElement.style.padding = '10px';
    inputElement.style.width = '200px';
    inputElement.style.marginBottom = '20px';
    inputElement.style.borderRadius = '4px';
    inputElement.style.border = '1px solid #666';
    inputElement.value = this.settings.playerName || 'Player';
    
    // Add event listener directly to the HTML element
    inputElement.addEventListener('input', () => {
      this.settings.playerName = inputElement.value || 'Player';
      this.settingsManager.updateSetting('playerName', this.settings.playerName);
    });
    
    // Create DOM element to hold the input
    this.playerNameInput = this.add.dom(panelX + 130, inputY, inputElement)
      .setOrigin(0.5);
  }
} 